import React from 'react'
import { Platform, StyleProp, StyleSheet, Switch, SwitchProps, View, ViewStyle } from 'react-native'
import { useTheme } from '@ui-kitten/components'
import { Text } from '@src/components/base'

export interface ToggleProps extends Omit<SwitchProps, 'value' | 'onValueChange' | 'onChange'> {
  checked?: boolean
  onChange?: (checked: boolean) => void
  style?: StyleProp<ViewStyle>
  text?: React.ReactNode
}

export const Toggle = ({
  checked = false,
  onChange,
  style,
  text,
  disabled,
  ...switchProps
}: ToggleProps) => {
  const theme = useTheme()
  const isDisabled = !!disabled
  let content: React.ReactNode = null

  if (text != null && typeof text !== 'boolean') {
    content = React.isValidElement(text) ? text : <Text type="regular">{String(text)}</Text>
  }
  const isAndroid = Platform.OS === 'android'
  const falseTrackColor = isDisabled
    ? theme['theme.disabled.background']
    : theme['theme.surface.base']
  const trueTrackColor = isDisabled
    ? theme['theme.disabled.background']
    : theme['theme.primary.base']
  const trackColor = checked ? trueTrackColor : falseTrackColor
  const thumbColor = isDisabled ? theme['theme.disabled.text'] : theme['theme.solid.white']

  return (
    <View style={[styles.container, style]}>
      <View style={styles.switchContainer}>
        {isAndroid ? (
          <View style={[styles.trackBackground, { backgroundColor: trackColor }]} />
        ) : null}
        <Switch
          {...switchProps}
          disabled={isDisabled}
          style={styles.switch}
          value={checked}
          onValueChange={onChange}
          ios_backgroundColor={trackColor}
          thumbColor={thumbColor}
          trackColor={{
            false: isAndroid ? 'transparent' : falseTrackColor,
            true: isAndroid ? 'transparent' : trueTrackColor,
          }}
        />
      </View>
      {content ? <View style={styles.textContainer}>{content}</View> : null}
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  switchContainer: {
    ...Platform.select({
      android: {
        width: 68,
        height: 40,
      },
      ios: {
        width: 56,
        height: 32,
      },
    }),
    alignItems: 'center',
    justifyContent: 'center',
  },
  trackBackground: {
    position: 'absolute',
    ...Platform.select({
      android: {
        width: 60,
        height: 32,
        borderRadius: 16,
      },
      ios: {
        width: 48,
        height: 26,
        borderRadius: 13,
      },
    }),
  },
  switch: {
    ...Platform.select({
      android: {
        transform: [{ scaleX: 1.46 }, { scaleY: 1.4 }],
      },
      ios: {
        transform: [{ scaleX: 0.95 }, { scaleY: 1.02 }],
      },
    }),
  },
  textContainer: {
    marginLeft: 8,
  },
})
